// src/components/hub/work/hooks/useWorkPageFetches.js
import { useEffect, useMemo, useState } from "react";
import {
  getWorkVerdict,
  getWorkTakeaways,
  getSavedViews,
  getWorkLoad,
} from "../../../../api/hub/work";
import { getAssignableMembers } from "../../../../api/conversations";

// Groups the work page's secondary fetch effects (verdict, takeaways, saved
// views, team roster, work-load strip) and their state. All are independent
// cancelled-flag fetch effects with no shared logic — this is pure grouping,
// each effect moved verbatim with its comments.
const useWorkPageFetches = ({ clientId, clientFilter, retryNonce }) => {
  const initialState = { loading: false, error: "", data: null };
  const [takeaways, setTakeaways] = useState(initialState);
  const [dismissedTakeaways, setDismissedTakeaways] = useState(new Set());
  const [verdict, setVerdict] = useState(null);
  const [verdictLoading, setVerdictLoading] = useState(true);
  const [loadData, setLoadData] = useState(null);

  // ─── Saved views ──────────────────────────────────────────────────────────────
  const [savedViews, setSavedViews] = useState([]);

  // ─── Team roster (ops_team_members → {id, name}) ─────────────────────────────
  // Authoritative owner-name source. fn_work_list returns an owner_id but often
  // not the display name, so the bucket-derived allFilterOptions.owners can't
  // always resolve an owner. Fetched once on mount; on failure this stays empty
  // and getOwnerName degrades to allFilterOptions.owners → "Unknown".
  const [teamMembers, setTeamMembers] = useState([]);

  // ─── Load verdict ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) { setVerdictLoading(false); return; }
    let cancelled = false;
    setVerdictLoading(true);
    getWorkVerdict()
      .then((resp) => { if (!cancelled) setVerdict(resp?.data ?? null); })
      .catch(() => { if (!cancelled) setVerdict(null); })
      .finally(() => { if (!cancelled) setVerdictLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, retryNonce]);

  // ─── Load takeaways ─────────────────────────────────────────────────────────
  // Scoped by the page's client filter (clientFilter), NOT the workspace-scope
  // clientId (view.supabaseClientId) — that holds the shell's selected/default
  // client even on the all-work view, which wrongly scoped takeaways to one
  // client (work-page-fixes §A). clientFilter is null on the all-work view →
  // pass null (fn_work_takeaways then returns the all-work set, no client
  // restriction); a set clientFilter scopes takeaways to that client, matching
  // how fn_work_list is scoped in useWorkBucketCache. Explicit `|| null` so we
  // never pass undefined or an empty string.
  useEffect(() => {
    let cancelled = false;
    setTakeaways({ loading: true, error: "", data: null });
    getWorkTakeaways(clientFilter || null)
      .then((resp) => {
        if (!cancelled) setTakeaways({ loading: false, error: "", data: resp?.data ?? null });
      })
      .catch(() => {
        if (!cancelled) setTakeaways({ loading: false, error: "", data: null });
      });
    return () => { cancelled = true; };
  }, [clientFilter, retryNonce]);

  // ─── Dismiss takeaway (session-only) ────────────────────────────────────────
  const dismissTakeaway = (panelKey) =>
    setDismissedTakeaways((prev) => new Set([...prev, panelKey]));

  const visibleTakeaways = useMemo(
    () => (takeaways.data || []).filter((tw) => !dismissedTakeaways.has(tw.panel)),
    [takeaways.data, dismissedTakeaways]
  );

  // ─── Saved views ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    getSavedViews()
      .then((resp) => { if (!cancelled) setSavedViews(resp?.data || []); })
      .catch(() => { if (!cancelled) setSavedViews([]); });
    return () => { cancelled = true; };
  }, [clientId, retryNonce]);

  // ─── Team roster (owner-name resolution for the filter pill) ─────────────────
  // Fetched once on mount. Secondary surface — a failure must never block the
  // page; it leaves teamMembers empty and getOwnerName degrades to the bucket-
  // derived list / "Unknown". See docs/CLIENTHUB-RPC-GAPS.md.
  useEffect(() => {
    let cancelled = false;
    getAssignableMembers()
      .then((rows) => {
        if (!cancelled) {
          // The endpoint sometimes returns rows as a raw array and sometimes
          // wrapped in a response envelope ({ data: [...] }). Unwrap both so the
          // roster is never dropped silently (which would make getOwnerName fall
          // through to "Unknown" for newly assigned owners).
          const list = Array.isArray(rows)
            ? rows
            : Array.isArray(rows?.data) ? rows.data : [];
          setTeamMembers(list);
        }
      })
      .catch(() => { if (!cancelled) setTeamMembers([]); });
    return () => { cancelled = true; };
  }, []);

  // ─── Load work-load strip ───────────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    getWorkLoad(clientId)
      .then((resp) => { if (!cancelled) setLoadData(resp?.data ?? null); })
      .catch(() => { if (!cancelled) setLoadData(null); });
    return () => { cancelled = true; };
  }, [clientId, retryNonce]);

  return {
    verdict,
    verdictLoading,
    takeaways,
    visibleTakeaways,
    dismissTakeaway,
    loadData,
    savedViews,
    teamMembers,
  };
};

export default useWorkPageFetches;
